import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button,
  Grid, Alert, Divider, Chip, Autocomplete,
  Stack, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, MenuItem,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon    from '@mui/icons-material/CancelOutlined';
import api from '../../services/api.js';
import { TableKit, toHebrewNumeral } from '@NP-Webber/crm-kit';
import AudioPlayer from '../../components/AudioPlayer.jsx';
import GradeFormDialog from '../../components/GradeFormDialog.jsx';
import GemaraExamineEditDialog from '../../components/GemaraExamineEditDialog.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatHebrewDate, formatHebrewDateTime } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';
const PHONE_ALT_LABEL = IS_MISHTAMERET ? 'טלפון אשה' : 'טלפון נוסף';
const OCCUPATION_OPTIONS = IS_MISHTAMERET
  ? ['עמל הפרנסה', 'כולל', 'ישיבת היכל התורה']
  : ['בעה"ב', 'אברך כולל'];

const MemberCard = ({ recordId, onClose }) => {
  const params   = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canReadGrades = hasPermission('grades', 'read');
  const canReadRegs = hasPermission('examRegistration', 'read');
  const id  = recordId || params.id;
  const isNew = id === 'new';

  const [tab, setTab] = useState('details');
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ציונים ומבחנים
  const [grades, setGrades] = useState([]);
  const [gradesTotal, setGradesTotal] = useState(0);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [gradesRefreshKey, setGradesRefreshKey] = useState(0);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);

  const [registrations, setRegistrations] = useState([]);
  const [regsTotal, setRegsTotal] = useState(0);
  const [regsLoading, setRegsLoading] = useState(false);

  // ── סימון נבחן בטאב הרשמות ──────────────────────────────────────────────
  const [regExamineDialogReg, setRegExamineDialogReg] = useState(null);
  const [regExamineSaving, setRegExamineSaving]       = useState(false);

  const [kolelOptions, setKolelOptions] = useState([]);
  const [kolelLoadError, setKolelLoadError] = useState('');

  // ── בקשות עדכון פרטים ──────────────────────────────────────────────────────
  const FIELD_LABEL = {
    firstName: 'שם פרטי', lastName: 'שם משפחה',
    fatherName: 'שם האב', fatherInLawName: 'שם השווער',
    city: 'ישוב', street: 'רחוב',
  };
  const [approveOpen,    setApproveOpen]    = useState(false);
  const [approveTarget,  setApproveTarget]  = useState(null);
  const [approveValue,   setApproveValue]   = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError,   setApproveError]   = useState('');
  const [rejectOpen,     setRejectOpen]     = useState(false);
  const [rejectTarget,   setRejectTarget]   = useState(null);
  const [rejectLoading,  setRejectLoading]  = useState(false);
  const [rejectError,    setRejectError]    = useState('');

  useEffect(() => {
    if (!id) return;
    setKolelLoadError('');
    if (!IS_MISHTAMERET) {
      api.get('/kolels', { params: { all: '1', status: 'active', pageSize: '200' } })
        .then(({ data }) => setKolelOptions(data.data || []))
        .catch((err) => {
          setKolelOptions([]);
          setKolelLoadError(
            err.response?.status === 403
              ? 'אין הרשאה לטעינת רשימת כוללים — לא ניתן לבחור כולל'
              : 'שגיאה בטעינת רשימת כוללים'
          );
        });
    }
    if (!isNew) {
      loadMember();
    } else {
      setForm({
        firstName: '', lastName: '', fatherName: '', fatherInLawName: '',
        phone: '', phoneAlt: '', email: '', idNumber: '',
        city: '', street: '', buildingNumber: '', entrance: '', apartment: '',
        occupation: '',
        kolel: '', kolelRef: null,
        kolelMorning: '', kolelMorningRef: null,
        kolelAfternoon: '', kolelAfternoonRef: null,
        kolelEvening: '', kolelEveningRef: null,
        registered: '', role: 'member', status: 'active',
      });
    }
  }, [id]);

  const loadMember = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/members/${id}`);
      setMember(data);
      setForm(data);
    } catch (err) {
      setError('שגיאה בטעינת נתוני חבר');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      let saveForm;
      if (IS_OPEN_GEMARA) {
        // תלמודא — כולל יחיד → ראשי + סלוט בוקר; מנקים אחה״צ/ערב
        const refId = form.kolelRef?._id || form.kolelRef || null;
        const name = form.kolelRef?.name || form.kolel || '';
        saveForm = {
          ...form,
          kolelRef: refId,
          kolel: name,
          kolelMorningRef: refId,
          kolelMorning: name,
          kolelAfternoonRef: null,
          kolelAfternoon: '',
          kolelEveningRef: null,
          kolelEvening: '',
        };
      } else {
        // חילוץ ObjectId מ-ref objects לפני שליחה
        saveForm = {
          ...form,
          kolelMorningRef: form.kolelMorningRef?._id || form.kolelMorningRef || null,
          kolelMorning: form.kolelMorningRef?.name || form.kolelMorning || '',
          kolelAfternoonRef: form.kolelAfternoonRef?._id || form.kolelAfternoonRef || null,
          kolelAfternoon: form.kolelAfternoonRef?.name || form.kolelAfternoon || '',
          kolelEveningRef: form.kolelEveningRef?._id || form.kolelEveningRef || null,
          kolelEvening: form.kolelEveningRef?.name || form.kolelEvening || '',
        };
        // כולל ראשי — מהסלוט הראשון המלא
        const primary =
          (saveForm.kolelMorningRef && { ref: saveForm.kolelMorningRef, name: saveForm.kolelMorning }) ||
          (saveForm.kolelAfternoonRef && { ref: saveForm.kolelAfternoonRef, name: saveForm.kolelAfternoon }) ||
          (saveForm.kolelEveningRef && { ref: saveForm.kolelEveningRef, name: saveForm.kolelEvening }) ||
          null;
        saveForm.kolelRef = primary?.ref || null;
        saveForm.kolel = primary?.name || '';
      }
      if (isNew) {
        const { data } = await api.post('/members', saveForm);
        setSuccess('חבר נוצר בהצלחה');
        if (onClose) onClose('created');
        else navigate(`/members/${data._id}`);
      } else {
        const { data } = await api.put(`/members/${id}`, { ...saveForm, version: member.version });
        setMember(data);
        setForm(data);
        setSuccess('נשמר בהצלחה');
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError('התנגשות גרסאות — רענן ונסה שוב');
      } else {
        setError(err.response?.data?.error || 'שגיאת שמירה');
      }
    }
  };

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  // ── approve / reject pending update ────────────────────────────────────────
  const openApprove = (item) => {
    setApproveTarget(item);
    setApproveValue('');
    setApproveError('');
    setApproveOpen(true);
  };
  const handleApprove = async () => {
    if (!approveValue.trim()) { setApproveError('יש להזין את הערך החדש'); return; }
    setApproveLoading(true); setApproveError('');
    try {
      await api.post(`/members/${id}/pending-updates/${approveTarget._id}/resolve`,
        { action: 'approve', value: approveValue.trim() });
      setApproveOpen(false);
      loadMember();
    } catch (e) {
      setApproveError(e.response?.data?.error || 'שגיאה באישור');
    } finally { setApproveLoading(false); }
  };
  const openReject = (item) => {
    setRejectTarget(item);
    setRejectError('');
    setRejectOpen(true);
  };
  const handleReject = async () => {
    setRejectLoading(true); setRejectError('');
    try {
      await api.post(`/members/${id}/pending-updates/${rejectTarget._id}/resolve`,
        { action: 'reject' });
      setRejectOpen(false);
      loadMember();
    } catch (e) {
      setRejectError(e.response?.data?.error || 'שגיאה בדחייה');
    } finally { setRejectLoading(false); }
  };

  // שליפת ציונים
  const fetchGrades = useCallback(async ({ page, pageSize }) => {
    if (isNew) return;
    setGradesLoading(true);
    try {
      const { data } = await api.get('/grades', { params: { memberId: id, page, pageSize } });
      setGrades(data.data);
      setGradesTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setGradesLoading(false);
    }
  }, [id, isNew]);

  const handleExportGrades = useCallback(async () => {
    const { data } = await api.get('/grades', { params: { memberId: id, all: 1 } });
    return data.data;
  }, [id]);

  // שליפת הרשמות
  const fetchRegistrations = useCallback(async ({ page, pageSize }) => {
    if (isNew) return;
    setRegsLoading(true);
    try {
      const { data } = await api.get('/registrations', { params: { memberId: id, page, pageSize } });
      setRegistrations(data.data);
      setRegsTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setRegsLoading(false);
    }
  }, [id, isNew]);

  const handleExportRegistrations = useCallback(async () => {
    const { data } = await api.get('/registrations', { params: { memberId: id, all: 1 } });
    return data.data;
  }, [id]);

  // ── handlers סימון נבחן ──────────────────────────────────────────────────
  const handleRegToggleExamined = useCallback(async (reg) => {
    // עדכון אופטימיסטי
    setRegistrations((prev) => prev.map((r) => r._id === reg._id ? { ...r, examined: !reg.examined } : r));
    try {
      const { data: updated } = await api.put(`/registrations/${reg._id}`, { examined: !reg.examined });
      setRegistrations((prev) => prev.map((r) => r._id === updated._id ? { ...r, examined: updated.examined } : r));
    } catch {
      setRegistrations((prev) => prev.map((r) => r._id === reg._id ? { ...r, examined: reg.examined } : r));
    }
  }, []);

  const openRegExamineDialog = useCallback((reg) => {
    setRegExamineDialogReg(reg);
  }, []);

  const handleRegExamineDialogSave = async (gemaraSelection) => {
    if (!regExamineDialogReg) return;
    setRegExamineSaving(true);
    try {
      const { data: updated } = await api.put(`/registrations/${regExamineDialogReg._id}`, { gemaraSelection });
      setRegistrations((prev) => prev.map((r) => r._id === updated._id ? { ...r, gemaraSelection: updated.gemaraSelection } : r));
      setRegExamineDialogReg(null);
    } catch {
      /* ignore — dialog stays open */
    } finally {
      setRegExamineSaving(false);
    }
  };

  const gradeColumns = [
    {
      key: '_examCode', title: 'קוד מבחן', sortable: false,
      render: (_, row) => row.examId?.code != null
        ? <Chip label={row.examId.code} size="small" variant="outlined" />
        : '—',
      exportRender: (_, row) => row.examId?.code != null ? String(row.examId.code) : '',
    },
    { key: 'examId', title: 'מבחן', render: (v) => v?.name || '—', sortable: false },
    { key: 'examPeriodId', title: 'מועד', render: (v) => v?.name || '—', sortable: false },
    { key: 'tractate', title: 'מסכת', sortable: false, render: (v) => v || '—' },
    { key: 'score', title: 'ציון', sortable: true, render: (v) => v >= 90 ? <Chip label={v} color="success" size="small" /> : v },
    {
      key: 'source',
      title: 'מקור',
      sortable: false,
      render: (v) => (v === 'ivr' ? 'IVR' : v === 'import' ? 'ייבוא' : 'ווב'),
    },
    { key: 'enteredAt', title: 'תאריך', sortable: true, render: (v) => formatHebrewDate(v) },
    ...(!IS_MISHTAMERET
      ? [{
          key: '_actions',
          title: '',
          sortable: false,
          render: (_, row) => (
            <Button size="small" onClick={() => { setEditingGrade(row); setGradeDialogOpen(true); }}>
              ערוך
            </Button>
          ),
        }]
      : []),
  ];

  const regColumns = [
    ...(IS_OPEN_GEMARA
      ? [{
          key: '_examCode', title: 'קוד מבחן', sortable: false,
          render: (_, row) => row.examId?.code != null
            ? <Chip label={row.examId.code} size="small" variant="outlined" />
            : '—',
          exportRender: (_, row) => row.examId?.code != null ? String(row.examId.code) : '',
        },
        {
          key: 'gemaraSelection',
          title: 'חומר',
          sortable: false,
          render: (v) => {
            if (!v || !v.length) return '—';
            return v.map((sel) => {
              const ranges = sel.ranges?.map((r) => `${toHebrewNumeral(r.from)}–${toHebrewNumeral(r.to)}`).join(', ');
              const examinedMark = sel.examined ? ' ✔' : '';
              return `${sel.tractate}${ranges ? ` (${ranges})` : ''}${examinedMark}`;
            }).join(' | ');
          },
        }]
      : [
          {
            key: '_examCode', title: 'קוד מבחן', sortable: false,
            render: (_, row) => row.examId?.code != null
              ? <Chip label={row.examId.code} size="small" variant="outlined" />
              : '—',
            exportRender: (_, row) => row.examId?.code != null ? String(row.examId.code) : '',
          },
          { key: 'examId', title: 'מבחן', render: (v) => v?.name || '—', sortable: false },
          { key: 'examined', title: 'נבחן', sortable: false, render: (v) => v ? <Chip label="כן" size="small" color="success" variant="outlined" /> : '—' },
        ]
    ),
    { key: 'periodId', title: 'מועד', sortable: false, render: (v) => v?.name || '—' },
    { key: 'location', title: 'עיר בחינה', sortable: false, render: (v) => v || '—' },
    { key: 'status', title: 'סטטוס', sortable: false, render: (v) => v === 'registered' ? '✅ רשום' : '❌ בוטל' },
    { key: 'source', title: 'מקור', sortable: false, render: (v) => v === 'ivr' ? '📞' : '🌐' },
    { key: 'registeredAt', title: 'תאריך', sortable: true, render: (v) => formatHebrewDate(v) },
    {
      key: '_markExamined', title: 'סמן נבחן', sortable: false,
      render: (_, reg) => {
        if (reg.status !== 'registered') return null;
        if (IS_OPEN_GEMARA) {
          const examinedCount = (reg.gemaraSelection || []).filter((s) => s.examined).length;
          const total = (reg.gemaraSelection || []).length;
          return (
            <Button size="small" variant={examinedCount > 0 ? 'contained' : 'outlined'} color="success"
              onClick={() => openRegExamineDialog(reg)} sx={{ fontSize: '0.72rem' }}>
              {examinedCount > 0 ? `${examinedCount}/${total} ✔` : 'סמן נבחן'}
            </Button>
          );
        }
        return (
          <Tooltip title={reg.examined ? 'סמן כלא נבחן' : 'סמן כנבחן'}>
            <Checkbox checked={!!reg.examined} onChange={() => handleRegToggleExamined(reg)} size="small" color="success" sx={{ p: 0.3 }} />
          </Tooltip>
        );
      },
    },
  ];

  if (loading) return <Box><Typography>טוען...</Typography></Box>;

  const personalFields = [
    ['firstName', 'שם פרטי'], ['lastName', 'משפחה'],
    ['fatherName', 'אביו'], ['fatherInLawName', 'חתן'],
    ['phone', 'נייד'], ['phoneAlt', PHONE_ALT_LABEL],
    ...(IS_MISHTAMERET ? [['email', 'מייל']] : []),
    ['idNumber', 'מ.ז.'],
  ];
  const addressFields = [
    ['city', 'ישוב'], ['street', 'רחוב'],
    ['buildingNumber', 'מספר בניין'], ['entrance', 'כניסה'], ['apartment', 'דירה'],
  ];
  // עוזר: מציאת כולל נבחר לפי _id (תומך באובייקט מאוכלס ו-id מחרוזת)
  const findKolel = (ref) => {
    if (!ref) return null;
    const kid = String(ref?._id || ref);
    return kolelOptions.find((k) => String(k._id) === kid) || (typeof ref === 'object' ? ref : null);
  };

  const classifySchedule = (schedule) => {
    const s = (schedule || '').trim();
    if (s.includes('בוקר') || s.includes('לפנה') || s.includes('לפני')) return 'morning';
    if (s.includes('אחה') || s.includes('צהר')) return 'afternoon';
    if (s.includes('ערב')) return 'evening';
    return 'unknown';
  };

  const kolelsForSlot = (slot, currentRef) => {
    const current = findKolel(currentRef);
    const list = kolelOptions.filter((k) => {
      const c = classifySchedule(k.schedule);
      return c === slot || c === 'unknown' || (k.schedule || '').includes('כל היום');
    });
    if (current && !list.some((k) => String(k._id) === String(current._id))) {
      return [current, ...list];
    }
    return list;
  };

  // filterOptions עבור Autocomplete — חיפוש לפי שם, עיר או קוד מספרי
  const kolelFilterOpts = (opts, state) => {
    const q = state.inputValue.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        o.city?.toLowerCase().includes(q) ||
        String(o.code ?? '').includes(q)
    );
  };

  const setKolelSlot = (nameKey, refKey) => (_, val) => {
    setForm((f) => ({
      ...f,
      [refKey]: val || null,
      [nameKey]: val?.name || '',
    }));
  };

  const renderKolelAutocomplete = (label, nameKey, refKey, slot) => (
    <Grid item xs={12} sm={6} md={4}>
      <Autocomplete
        options={kolelsForSlot(slot, form[refKey])}
        getOptionLabel={(opt) => `${opt.name || ''}${opt.city ? ` (${opt.city})` : ''}${opt.code != null && opt.code !== '' ? ` — ${opt.code}` : ''}`}
        value={findKolel(form[refKey])}
        onChange={setKolelSlot(nameKey, refKey)}
        filterOptions={kolelFilterOpts}
        isOptionEqualToValue={(opt, val) => String(opt?._id) === String(val?._id)}
        clearOnEscape
        noOptionsText={kolelLoadError || 'לא נמצא כולל'}
        // בתוך Dialog — רשימה ב-portal מחוץ למודל עלולה לא לקבל לחיצה בלי disableEnforceFocus
        componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            helperText={
              form[nameKey] && !findKolel(form[refKey])
                ? `מהאקסל: ${form[nameKey]}`
                : ''
            }
          />
        )}
      />
    </Grid>
  );

  const renderSingleKolelAutocomplete = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Autocomplete
        options={kolelOptions}
        getOptionLabel={(opt) => `${opt.name || ''}${opt.city ? ` (${opt.city})` : ''}${opt.code != null && opt.code !== '' ? ` — ${opt.code}` : ''}`}
        value={findKolel(form.kolelRef) || findKolel(form.kolelMorningRef)}
        onChange={setKolelSlot('kolel', 'kolelRef')}
        filterOptions={kolelFilterOpts}
        isOptionEqualToValue={(opt, val) => String(opt?._id) === String(val?._id)}
        clearOnEscape
        noOptionsText={kolelLoadError || 'לא נמצא כולל'}
        componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="כולל"
            size="small"
            helperText={
              form.kolel && !findKolel(form.kolelRef) && !findKolel(form.kolelMorningRef)
                ? `מהאקסל: ${form.kolel}`
                : ''
            }
          />
        )}
      />
    </Grid>
  );

  return (
    <Box>
      <AdminPageHeader
        title={isNew ? 'חבר חדש' : `${form.firstName || ''} ${form.lastName || ''}`}
        sub={isNew ? 'כרטיס חבר' : [form.phone, form.city, form.idNumber ? `ת.ז. ${form.idNumber}` : null].filter(Boolean).join(' · ') || 'כרטיס חבר'}
        back={!recordId ? { label: 'חברים', onClick: () => navigate('/members') } : undefined}
        legacy={(
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {isNew ? 'חבר חדש' : `${form.firstName || ''} ${form.lastName || ''}`}
          </Typography>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="details" label="פרטים" />
          {!isNew && canReadRegs && !IS_MISHTAMERET && <Tab value="regs" label="מבחנים" />}
          {!isNew && canReadGrades && <Tab value="grades" label="ציונים" />}
          {!isNew && (
            <Tab
              value="pending"
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  בקשות עדכון
                  {(member?.pendingUpdates || []).filter(u => u.status === 'pending').length > 0 && (
                    <Chip
                      label={(member.pendingUpdates).filter(u => u.status === 'pending').length}
                      color="warning"
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              }
            />
          )}
        </Tabs>
      </Paper>

      {/* Tab: פרטים */}
      {tab === 'details' && (
        <Paper sx={{ p: 3 }}>
          {/* פרטים אישיים */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>פרטים אישיים</Typography>
          <Grid container spacing={2}>
            {personalFields.map(([key, label]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <TextField
                  label={label}
                  fullWidth
                  size="small"
                  value={form[key] || ''}
                  onChange={handleChange(key)}
                />
              </Grid>
            ))}
            {/* קוד אישי — קריאה בלבד */}
            {!isNew && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="קוד אישי"
                  fullWidth
                  size="small"
                  value={form.personalCode != null ? String(form.personalCode).padStart(4, '0') : 'לא הוקצה'}
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'var(--ck-primary, #1565c0)', fontWeight: 700 } }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="עיסוק"
                fullWidth
                size="small"
                value={form.occupation || ''}
                onChange={handleChange('occupation')}
              >
                <MenuItem value="">ללא</MenuItem>
                {[
                  ...OCCUPATION_OPTIONS,
                  ...(form.occupation && !OCCUPATION_OPTIONS.includes(form.occupation) ? [form.occupation] : []),
                ].map((o) => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* כתובת */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>כתובת</Typography>
          <Grid container spacing={2}>
            {addressFields.map(([key, label]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <TextField
                  label={label}
                  fullWidth
                  size="small"
                  value={form[key] || ''}
                  onChange={handleChange(key)}
                />
              </Grid>
            ))}
          </Grid>

          {!IS_MISHTAMERET && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* כוללים — תלמודא: שדה יחיד; הלכתא: לפי סדר */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {IS_OPEN_GEMARA ? 'כולל' : 'כוללים'}
              </Typography>
              {kolelLoadError && (
                <Alert severity="warning" sx={{ mb: 1 }}>{kolelLoadError}</Alert>
              )}
              <Grid container spacing={2}>
                {IS_OPEN_GEMARA ? (
                  renderSingleKolelAutocomplete()
                ) : (
                  <>
                    {renderKolelAutocomplete('כולל לפנה"צ', 'kolelMorning', 'kolelMorningRef', 'morning')}
                    {renderKolelAutocomplete('כולל אחה"צ', 'kolelAfternoon', 'kolelAfternoonRef', 'afternoon')}
                    {renderKolelAutocomplete('כולל ערב', 'kolelEvening', 'kolelEveningRef', 'evening')}
                  </>
                )}
              </Grid>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSave}>
              {isNew ? 'צור חבר' : 'שמור שינויים'}
            </Button>
            <Button variant="outlined" onClick={() => onClose ? onClose() : navigate('/members')}>
              חזרה
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tab: מבחנים (הרשמות) */}
      {tab === 'regs' && !isNew && canReadRegs && (
        <TableKit
          columns={regColumns}
          data={registrations}
          total={regsTotal}
          loading={regsLoading}
          onFetch={fetchRegistrations}
          onExportFetch={handleExportRegistrations}
          clientSideMode={false}
          emptyMessage="אין הרשמות"
          showFilters={false}
          exportFileName={`הרשמות_${form.lastName}`}
        />
      )}

      {/* Tab: ציונים */}
      {tab === 'grades' && !isNew && canReadGrades && (
        <Box>
          {!IS_MISHTAMERET && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => { setEditingGrade(null); setGradeDialogOpen(true); }}
              >
                + הוסף ציון
              </Button>
            </Box>
          )}
          <TableKit
            key={gradesRefreshKey}
            columns={gradeColumns}
            data={grades}
            total={gradesTotal}
            loading={gradesLoading}
            onFetch={fetchGrades}
            onExportFetch={handleExportGrades}
            clientSideMode={false}
            emptyMessage="אין ציונים"
            showFilters={false}
            exportFileName={`ציונים_${form.lastName}`}
          />
        </Box>
      )}

      {/* Tab: בקשות עדכון פרטים מהמערכת הקולית */}
      {tab === 'pending' && !isNew && (() => {
        const pendingItems = (member?.pendingUpdates || []).filter(u => u.status === 'pending');
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>בקשות עדכון פרטים — ממתינות לאישור</Typography>
            {pendingItems.length === 0 ? (
              <Alert severity="info">אין בקשות ממתינות</Alert>
            ) : (
              <Stack spacing={2}>
                {pendingItems.map((item) => (
                  <Paper key={item._id} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                      <Box flex={1} minWidth={120}>
                        <Typography variant="caption" color="text.secondary">שדה</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {FIELD_LABEL[item.field] || item.label || item.field}
                        </Typography>
                      </Box>
                      <Box flex={1} minWidth={120}>
                        <Typography variant="caption" color="text.secondary">תאריך בקשה</Typography>
                        <Typography variant="body2">
                          {item.requestedAt ? formatHebrewDateTime(item.requestedAt) : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">הקלטה</Typography>
                        <Box>{item.recordingUrl ? <AudioPlayer path={item.recordingUrl} /> : <Typography variant="body2">—</Typography>}</Box>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="אשר ועדכן">
                          <IconButton size="small" color="success" onClick={() => openApprove(item)}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="דחה">
                          <IconButton size="small" color="error" onClick={() => openReject(item)}>
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        );
      })()}

      {/* ── דיאלוג אישור ────────────────────────────────────────────────── */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>אישור עדכון פרטים</DialogTitle>
        <DialogContent>
          {approveTarget && (
            <Box mb={2}>
              <Typography variant="body2" gutterBottom>
                שדה: <strong>{FIELD_LABEL[approveTarget.field] || approveTarget.label}</strong>
              </Typography>
              {approveTarget.recordingUrl && (
                <Box mb={1}><AudioPlayer path={approveTarget.recordingUrl} label="האזן להקלטה" /></Box>
              )}
            </Box>
          )}
          <TextField
            label="ערך חדש"
            fullWidth
            size="small"
            value={approveValue}
            onChange={(e) => setApproveValue(e.target.value)}
            placeholder="הקלידו את הערך לעדכון"
            autoFocus
          />
          {approveError && <Alert severity="error" sx={{ mt: 1 }}>{approveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={approveLoading}>ביטול</Button>
          <Button onClick={handleApprove} variant="contained" color="success" disabled={approveLoading}>
            {approveLoading ? 'שומר...' : 'אשר ועדכן'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── דיאלוג דחייה ────────────────────────────────────────────────── */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>דחיית בקשה</DialogTitle>
        <DialogContent>
          {rejectTarget && (
            <Typography variant="body2" color="text.secondary">
              לדחות את בקשת העדכון לשדה <strong>{FIELD_LABEL[rejectTarget.field] || rejectTarget.label}</strong>?
            </Typography>
          )}
          {rejectError && <Alert severity="error" sx={{ mt: 1 }}>{rejectError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={rejectLoading}>ביטול</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={rejectLoading}>
            {rejectLoading ? 'דוחה...' : 'דחה'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── דיאלוג סימון נבחן — תלמודא (טווחים + נבחן) ─────────────────────── */}
      {IS_OPEN_GEMARA && (
        <GemaraExamineEditDialog
          key={regExamineDialogReg?._id || 'examine'}
          open={!!regExamineDialogReg}
          memberName={`${form.firstName || ''} ${form.lastName || ''}`.trim()}
          initialSelection={regExamineDialogReg?.gemaraSelection || []}
          onSave={handleRegExamineDialogSave}
          saving={regExamineSaving}
          onClose={() => !regExamineSaving && setRegExamineDialogReg(null)}
        />
      )}

      {canReadGrades && (
        <GradeFormDialog
          open={gradeDialogOpen}
          onClose={() => { setGradeDialogOpen(false); setEditingGrade(null); }}
          member={!isNew ? { _id: id, firstName: form.firstName, lastName: form.lastName, personalCode: form.personalCode } : null}
          grade={editingGrade}
          onSaved={() => setGradesRefreshKey((k) => k + 1)}
        />
      )}
    </Box>
  );
};

export default MemberCard;
